import { ENV } from '../../../../config/runtimeEnv';
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { Input, Button, Table, Tabs, Modal, Space, Tooltip, Typography, Drawer, Tag } from "antd";
import {
	PlusOutlined, EyeOutlined, BellOutlined, DeleteOutlined,
	ExclamationCircleOutlined, SearchOutlined, EditOutlined, TabletOutlined, QrcodeOutlined,
} from "@ant-design/icons";
import { getSessionUser } from "../../../../utils/auth/sessionUser";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import {
	AssignedForm, getAssignedForms, getPractitionerAssignedForms,
	fetchForms, assignForm, Form, FormElement, FormSection,
} from "../../../../api/formApi";
import AssignFormModal from "./submodals/AssignFormModal";
import ReviewFormModal from "../../formsInTray/ReviewFormModal";
import PractitionerFillFormModal from "../../formsInTray/PractitionerFillFormModal";
import PatientFormFillModal from "../../formsInTray/PatientFormFillModal";
import UnifiedFormRenderer from "../../../../components/forms/FormRenderer";
import { loadUserPreferences } from "../../../../utils/userPrefs/UserPrefs";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import { Company } from "../../../../types/admin/CompanyTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import { can } from "../../../../utils/userPrefs/validatePermissions";
import InlineLoading from "../../../../components/shared/InlineLoading";
import StatusTag from "../../../../components/shared/StatusTag";
import { sendCommunication } from "../../../../api/communications/communicationApi";
import { buildFormAssignedCommunication } from "../../../../utils/forms/formAssignedNotification";
import { isFormDone } from "../../../../utils/forms/formStatus";
import { HandoverStartModal } from "../../../../components/forms/HandoverExitGuard";
import FormShareLinkModal from "../../formsInTray/FormShareLinkModal";
import { http } from "../../../../lib/http/client";

const { Text } = Typography;

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

interface FormsTabProps {
	updatedPatient: Patient;
	setUpdatedPatient: React.Dispatch<React.SetStateAction<Patient>>;
	handleAutoSave: () => void;
}

const FormsTab: React.FC<FormsTabProps> = ({ updatedPatient }) => {
	const user = getSessionUser();
	const isClinical = can("healthcare.forms.write");
	const [pracCompany, setPracCompany] = useState<Company | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
	const [completedForms, setCompletedForms] = useState<AssignedForm[]>([]);
	const [pendingForms, setPendingForms] = useState<AssignedForm[]>([]);
	const [practitionerForms, setPractitionerForms] = useState<AssignedForm[]>([]);
	const [selectedForm, setSelectedForm] = useState<AssignedForm | null>(null);
	const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
	const [isFillModalOpen, setIsFillModalOpen] = useState(false);
	const [fillFormId, setFillFormId] = useState<number | null>(null);
	// F10 hand-over. Holds the whole row: the modal needs the assignment's `access_token`,
	// which `getAssignedForms` surfaces here (unlike the cross-patient tray listing).
	const [handoverForm, setHandoverForm] = useState<AssignedForm | null>(null);
	// F11 share link. The modal issues its own token via `link-issued`, so it needs nothing
	// from the row beyond the assignment id.
	const [shareForm, setShareForm] = useState<AssignedForm | null>(null);
	const [activeTab, setActiveTab] = useState("patient");
	const [patientSubTab, setPatientSubTab] = useState("pending");
	const [practitionerSubTab, setPractitionerSubTab] = useState("pending");
	const [searchTerm, setSearchTerm] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [catalogForms, setCatalogForms] = useState<Form[]>([]);
	const [catalogLoading, setCatalogLoading] = useState(false);
	const [previewForm, setPreviewForm] = useState<Form | null>(null);
	const [assigningFormId, setAssigningFormId] = useState<number | null>(null);
	const { showSuccess, showError } = useNotifications();

	const practitionerPendingForms = useMemo(
		() => practitionerForms.filter((f) => !isFormDone(f.status)),
		[practitionerForms],
	);
	const practitionerCompletedForms = useMemo(
		() => practitionerForms.filter((f) => isFormDone(f.status)),
		[practitionerForms],
	);

	useEffect(() => {
		fetchClinicCompany().then(setPracCompany).catch(() => { });
		loadAllForms();
		loadCatalogForms();
	}, []);

	const deleteAssignedForm = async (assignedFormId: number) => {
		await http.delete(`${API_BASE_URL}/practitioner/forms/assign/${assignedFormId}`);
	};

	const loadAllForms = async () => {
		setIsLoading(true);
		try {
			const [patientForms, pracForms] = await Promise.all([
				getAssignedForms(updatedPatient.id),
				isClinical
					? getPractitionerAssignedForms(undefined, updatedPatient.id)
					: Promise.resolve([]),
			]);

			// Split on the done predicate and its complement, the same way the practitioner group does.
			// An allowlist here ("Assigned"/"Pending") dropped "In Progress" and "Cancelled" — both
			// accepted by FormsController::updateAssignedFormStatus — out of the UI entirely.
			setCompletedForms(patientForms.filter((f) => isFormDone(f.status)));
			setPendingForms(patientForms.filter((f) => !isFormDone(f.status)));
			setPractitionerForms(pracForms);
		} catch {
			showError("Failed to load forms");
		} finally {
			setIsLoading(false);
		}
	};

	const loadCatalogForms = async () => {
		setCatalogLoading(true);
		try {
			const { data } = await fetchForms();
			const transformed = data.map((f) => ({
				...f,
				structure: typeof f.structure === "string" ? JSON.parse(f.structure as unknown as string) : f.structure,
			}));
			setCatalogForms(transformed);
		} catch {
			showError("Failed to load form catalog");
		} finally {
			setCatalogLoading(false);
		}
	};

	const openFormModal = useCallback(() => {
		if (!isProcessing) setIsAssignModalOpen(true);
	}, [isProcessing]);

	const closeFormModal = useCallback(() => {
		setIsAssignModalOpen(false);
		setTimeout(loadAllForms, 100);
	}, []);

	const openReviewModal = useCallback(
		async (form: AssignedForm, e?: React.MouseEvent) => {
			e?.preventDefault(); e?.stopPropagation();
			if (isProcessing) return;
			if (isFormDone(form.status)) {
				setSelectedForm({ ...form, patient: updatedPatient });
				setIsReviewModalOpen(true);
			} else {
				// F8: in-app, on the practitioner's own token. This used to `window.open` the
				// patient's tokenised link into a new tab — a chrome-free patient page that
				// wrote the submission as if the PATIENT had made it, and lost the practitioner
				// mid-consult. `PatientFormFillModal` fills the same form against the
				// `practitioner/forms/*` trio, stamping `submitted_by_role = 'practitioner'`.
				setSelectedForm({ ...form, patient: updatedPatient });
				setIsFillModalOpen(true);
			}
		},
		[isProcessing, updatedPatient],
	);

	const closeReviewModal = useCallback(() => {
		setSelectedForm(null);
		setIsReviewModalOpen(false);
		loadAllForms();
	}, []);

	const closeFillModal = useCallback(() => {
		setSelectedForm(null);
		setIsFillModalOpen(false);
	}, []);

	const handleRemindPatient = useCallback(
		(form: AssignedForm, e?: React.MouseEvent) => {
			e?.preventDefault(); e?.stopPropagation();
			if (isProcessing) return;
			Modal.confirm({
				title: "Confirm Reminder",
				icon: <ExclamationCircleOutlined />,
				content: "Send a reminder to the patient for this form?",
				okText: "Send Reminder",
				okType: "primary",
				cancelText: "Cancel",
				onOk: async () => {
					setIsProcessing(true);
					try {
						// One shared builder with the other three send sites (F11): see
						// `utils/forms/formAssignedNotification`. This is the ONE site on the
						// user-id-addressed `/communications/send` transport — the payload shape
						// differs, the `data` bag does not.
						const payload = buildFormAssignedCommunication({
							assignedFormId: form.id,
							accessToken: form.access_token,
							patient: updatedPatient,
							recipientUserId: updatedPatient.id,
							companyId: pracCompany?.id,
							userId: user.id,
						});
						if (ENV.VITE_NOTIFICATIONS_ENABLED === "true")
							sendCommunication(payload);
						showSuccess("Reminder sent successfully");
					} catch {
						showError("Failed to send reminder");
					} finally {
						setIsProcessing(false);
					}
				},
			});
		},
		[updatedPatient, pracCompany, isProcessing],
	);

	const handleDeleteAssignment = useCallback(
		(form: AssignedForm, e?: React.MouseEvent) => {
			e?.preventDefault(); e?.stopPropagation();
			if (isProcessing) return;
			Modal.confirm({
				title: "Confirm Removal",
				icon: <ExclamationCircleOutlined />,
				content: "Remove this form assignment?",
				okText: "Remove",
				okType: "danger",
				cancelText: "Cancel",
				onOk: async () => {
					setIsProcessing(true);
					try {
						await deleteAssignedForm(form.id);
						showSuccess("Form assignment removed");
						await loadAllForms();
					} catch {
						showError("Failed to remove form assignment");
					} finally {
						setIsProcessing(false);
					}
				},
			});
		},
		[isProcessing],
	);

	const handleAssignTemplate = useCallback(
		(template: Form, e?: React.MouseEvent) => {
			e?.preventDefault(); e?.stopPropagation();
			if (isProcessing) return;
			Modal.confirm({
				title: "Assign Form",
				icon: <ExclamationCircleOutlined />,
				content: `Assign "${template.title}" to ${updatedPatient.first_name} ${updatedPatient.last_name}?`,
				okText: "Assign",
				okType: "primary",
				cancelText: "Cancel",
				onOk: async () => {
					setAssigningFormId(template.id);
					try {
						const af: Partial<AssignedForm> = {
							form_id: template.id,
							patient_id: updatedPatient.id,
							practitioner_id: user.id,
							notes: "",
							// F6: was "Assigned" — see the note in `AssignFormModal`. One vocabulary,
							// enforced by `Rule::enum` on the backend; "Assigned" is now a 422.
							status: "Pending",
							assignee_type: "patient",
							created_at: new Date(),
							updated_at: new Date(),
						};
						await assignForm(af, updatedPatient.uuid);
						showSuccess(`"${template.title}" assigned successfully`);
						await loadAllForms();
					} catch {
						showError("Failed to assign form");
					} finally {
						setAssigningFormId(null);
					}
				},
			});
		},
		[isProcessing, updatedPatient, user.id],
	);

	const getFilteredForms = (list: AssignedForm[]) => {
		if (!searchTerm) return list;
		const q = searchTerm.toLowerCase();
		return list.filter((f) => f.form?.title?.toLowerCase().includes(q));
	};

	const getFilteredCatalogForms = () => {
		if (!searchTerm) return catalogForms;
		const q = searchTerm.toLowerCase();
		return catalogForms.filter((f) => f.title?.toLowerCase().includes(q) || f.department?.toLowerCase().includes(q));
	};

	const formatDate = (d: Date | string) => {
		try { return format(new Date(d), "MMM dd, yyyy"); } catch { return "—"; }
	};

	const getStatusTag = (f: AssignedForm) => <StatusTag status={f.status.toLowerCase()} />;

	const patientFormActions = useCallback(
		(form: AssignedForm) => {
			const done = isFormDone(form.status);
			return (
				<Space>
					{/* One handler, two distinct actions — `openReviewModal` branches on the same
					    done predicate. A dedicated "Fill on behalf" button rather than an
					    overloaded eye, so it reads as a sibling of "Hand to patient" (F10) and
					    "Open on patient's phone" (F11) once those land. */}
					{done ? (
						<Tooltip title="Review Form">
							<Button type="text" icon={<EyeOutlined />} onClick={(e) => openReviewModal(form, e)} disabled={isProcessing} size="small" data-testid="forms-tab-review-form" />
						</Tooltip>
					) : (
						<Tooltip title="Fill on behalf">
							<Button type="primary" icon={<EditOutlined />} onClick={(e) => openReviewModal(form, e)} disabled={isProcessing} size="small" data-testid="forms-tab-fill-on-behalf" />
						</Tooltip>
					)}
					{/* F10 — hand this device to the patient. A sibling of "Fill on behalf", not a
					    replacement: on-behalf records the practitioner as the author, hand-over
					    records the patient. The signature rule is what makes both necessary — a
					    practitioner may not sign, so a consent form has to be handed over. */}
					{!done && (
						<Tooltip title="Hand to patient">
							<Button type="text" icon={<TabletOutlined />} onClick={(e) => { e.stopPropagation(); setHandoverForm(form); }} disabled={isProcessing} size="small" data-testid="forms-tab-hand-to-patient" />
						</Tooltip>
					)}
					{/* F11 — the third sibling: the patient's OWN device. The safe counterpart to
					    hand-over, which leaves a live clinical session on the handed-over device. */}
					{!done && (
						<Tooltip title="Open on patient's phone">
							<Button type="text" icon={<QrcodeOutlined />} onClick={(e) => { e.stopPropagation(); setShareForm(form); }} disabled={isProcessing} size="small" data-testid="forms-tab-share-link" />
						</Tooltip>
					)}
					{!done && (
						<Tooltip title="Send Reminder">
							<Button type="text" icon={<BellOutlined />} onClick={(e) => handleRemindPatient(form, e)} disabled={isProcessing} size="small" data-testid="forms-tab-send-reminder" style={{ color: "var(--mg-warning)" }} />
						</Tooltip>
					)}
					<Tooltip title="Remove">
						<Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => handleDeleteAssignment(form, e)} disabled={isProcessing} size="small" />
					</Tooltip>
				</Space>
			);
		},
		[openReviewModal, handleRemindPatient, handleDeleteAssignment, isProcessing],
	);

	const practitionerFormActions = useCallback(
		(form: AssignedForm) => {
			const done = isFormDone(form.status);
			const canFill = form.is_mine && !done;
			return (
				<Space>
					{canFill ? (
						<Tooltip title="Fill Form">
							<Button type="primary" icon={<EditOutlined />} onClick={() => setFillFormId(form.id)} disabled={isProcessing} size="small" />
						</Tooltip>
					) : (
						<Tooltip title={done ? "View Response" : "View (assigned to another practitioner)"}>
							<Button type="text" icon={<EyeOutlined />} onClick={() => { setSelectedForm({ ...form, patient: updatedPatient }); setIsReviewModalOpen(true); }} disabled={isProcessing} size="small" />
						</Tooltip>
					)}
					<Tooltip title="Remove">
						<Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => handleDeleteAssignment(form, e)} disabled={isProcessing} size="small" />
					</Tooltip>
				</Space>
			);
		},
		[isProcessing, updatedPatient, handleDeleteAssignment],
	);

	const pagination = {
		pageSize: loadUserPreferences().tableRowsPerPage,
		showSizeChanger: true,
		showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
	};

	const baseColumns = [
		{ title: "Form Title", key: "title", render: (_: any, r: AssignedForm) => <Text>{r.form?.title}</Text>, sorter: (a: AssignedForm, b: AssignedForm) => (a.form?.title || "").localeCompare(b.form?.title || "") },
		{ title: "Status", key: "status", render: (_: any, r: AssignedForm) => getStatusTag(r), width: 120 },
		{ title: "Assigned", key: "assigned", render: (_: any, r: AssignedForm) => <Text style={{ fontSize: 12 }}>{formatDate(r.created_at)}</Text>, width: 100 },
		{ title: "Completed", key: "completed", render: (_: any, r: AssignedForm) => <Text style={{ fontSize: 12 }}>{formatDate(r.updated_at)}</Text>, width: 100 },
		{ title: "Actions", key: "actions", render: (_: any, r: AssignedForm) => patientFormActions(r), width: 120 },
	];

	const practitionerColumns = [
		{ title: "Form Title", key: "title", render: (_: any, r: AssignedForm) => <Text>{r.form?.title}</Text> },
		{
			title: "Assigned To",
			key: "assignee",
			render: (_: any, r: AssignedForm) => {
				const name = r.assigned_to_practitioner
					? `${(r.assigned_to_practitioner as any).first_name} ${(r.assigned_to_practitioner as any).last_name}`
					: "—";
				return (
					<Space>
						<Text>{name}</Text>
						{r.is_mine && <StatusTag status="you" size="small" />}
					</Space>
				);
			},
		},
		{ title: "Status", key: "status", render: (_: any, r: AssignedForm) => getStatusTag(r), width: 120 },
		{ title: "Assigned", key: "assigned", render: (_: any, r: AssignedForm) => <Text style={{ fontSize: 12 }}>{formatDate(r.created_at)}</Text>, width: 100 },
		{ title: "Actions", key: "actions", render: (_: any, r: AssignedForm) => practitionerFormActions(r), width: 120 },
	];

	const catalogColumns = [
		{
			title: "Form Title",
			key: "title",
			render: (_: any, r: Form) => (
				<Space>
					<Text>{r.title}</Text>
					{r.is_global && <Tag color="geekblue">Global</Tag>}
				</Space>
			),
			sorter: (a: Form, b: Form) => (a.title || "").localeCompare(b.title || ""),
		},
		{ title: "Department", key: "department", render: (_: any, r: Form) => <Text style={{ fontSize: 12 }}>{r.department || "—"}</Text> },
		{ title: "Type", key: "form_type", render: (_: any, r: Form) => <Text style={{ fontSize: 12 }}>{r.form_type === "practitioner" ? "Practitioner" : "Patient"}</Text>, width: 110 },
		{
			title: "Actions",
			key: "actions",
			render: (_: any, r: Form) => (
				<Space>
					<Tooltip title="Preview">
						<Button type="text" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setPreviewForm(r); }} disabled={isProcessing} size="small" />
					</Tooltip>
					<Tooltip title={r.form_type === "practitioner" ? 'Practitioner forms are assigned via the practitioner picker in "Assign Form", not directly to the patient' : "Assign to this patient"}>
						<Button type="primary" icon={<PlusOutlined />} onClick={(e) => handleAssignTemplate(r, e)} loading={assigningFormId === r.id} disabled={isProcessing || r.form_type === "practitioner"} size="small" />
					</Tooltip>
				</Space>
			),
			width: 140,
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={openFormModal} disabled={isProcessing}>
					Assign Form
				</Button>
				<Input placeholder="Search forms" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} prefix={<SearchOutlined />} style={{ minWidth: 240 }} allowClear />
			</div>

			<>
				{isLoading ? (
					<InlineLoading message="Loading forms…" minHeight={200} />
				) : (
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: "patient",
								label: "Patient",
								children: (
									<Tabs
										size="small"
										activeKey={patientSubTab}
										onChange={setPatientSubTab}
										items={[
											{ key: "pending", label: "Pending", children: <Table dataSource={getFilteredForms(pendingForms)} columns={baseColumns.filter((c) => c.key !== "completed")} rowKey="id" pagination={{ pageSize: 10 }} size="small" scroll={{ x: "max-content" }} locale={{ emptyText: "No pending forms" }} /> },
											{ key: "completed", label: "Completed", children: <Table dataSource={getFilteredForms(completedForms)} columns={baseColumns.filter((c) => c.key !== "status")} rowKey="id" pagination={{ pageSize: 10 }} size="small" scroll={{ x: "max-content" }} locale={{ emptyText: "No completed forms" }} /> },
										]}
									/>
								),
							},
							...(isClinical ? [{
								key: "practitioner",
								label: `Practitioner${practitionerForms.length > 0 ? ` (${practitionerForms.length})` : ""}`,
								children: (
									<Tabs
										size="small"
										activeKey={practitionerSubTab}
										onChange={setPractitionerSubTab}
										items={[
											{ key: "pending", label: "Pending", children: <Table dataSource={getFilteredForms(practitionerPendingForms)} columns={practitionerColumns} rowKey="id" pagination={pagination} size="small" scroll={{ x: "max-content" }} locale={{ emptyText: "No pending practitioner forms" }} /> },
											{ key: "completed", label: "Completed", children: <Table dataSource={getFilteredForms(practitionerCompletedForms)} columns={practitionerColumns.filter((c) => c.key !== "status")} rowKey="id" pagination={pagination} size="small" scroll={{ x: "max-content" }} locale={{ emptyText: "No completed practitioner forms" }} /> },
										]}
									/>
								),
							}] : []),
							...(isClinical ? [{ key: "catalog", label: "All Forms", children: <Table dataSource={getFilteredCatalogForms()} columns={catalogColumns} rowKey="id" pagination={pagination} loading={catalogLoading} size="small" scroll={{ x: "max-content" }} locale={{ emptyText: "No form templates found" }} /> }] : []),
						]}
					/>
				)}
			</>

			{isAssignModalOpen && pracCompany && (
				<AssignFormModal isOpen={isAssignModalOpen} onClose={closeFormModal} patient={updatedPatient} company={pracCompany} />
			)}
			{isReviewModalOpen && selectedForm && (
				<ReviewFormModal isOpen={isReviewModalOpen} onClose={closeReviewModal} onSave={closeReviewModal} assignedForm={selectedForm} />
			)}
			{isFillModalOpen && selectedForm && (
				<PatientFormFillModal isOpen={isFillModalOpen} onClose={closeFillModal} onSave={loadAllForms} assignedForm={selectedForm} />
			)}
			{handoverForm && (
				<HandoverStartModal
					open
					assignedForm={handoverForm}
					patientName={`${updatedPatient.first_name ?? ""} ${updatedPatient.last_name ?? ""}`.trim() || undefined}
					onCancel={() => setHandoverForm(null)}
				/>
			)}
			{shareForm && (
				<FormShareLinkModal
					isOpen
					assignedForm={shareForm}
					patient={updatedPatient}
					companyId={pracCompany?.id ?? 0}
					onClose={() => setShareForm(null)}
				/>
			)}
			{fillFormId !== null && (
				<PractitionerFillFormModal
					isOpen
					assignedFormId={fillFormId}
					patientName={`${updatedPatient.first_name} ${updatedPatient.last_name}`}
					onClose={() => { setFillFormId(null); loadAllForms(); }}
				/>
			)}
			{previewForm && (
				<Drawer
					data-testid="forms-catalog-preview-drawer"
					title={previewForm.title}
					open={!!previewForm}
					width={720}
					onClose={() => setPreviewForm(null)}
				>
					<UnifiedFormRenderer
						structure={
							(typeof previewForm.structure === "string"
								? JSON.parse(previewForm.structure)
								: previewForm.structure) as (FormElement | FormSection)[]
						}
						useSections={!!previewForm.use_sections}
						values={{}}
						onValuesChange={() => { }}
						readOnly
						sectionMode="flat"
						title={previewForm.title}
					/>
				</Drawer>
			)}
		</div>
	);
};

export default FormsTab;
